import { clientConfig } from '@/config/clientConfig';
import { dpopFetch } from '@/shared/lib/crypto/dpop';
import { isObject } from '@/shared/utils/guards';

import { apiConfig } from '../config';
import { ERROR_CODES } from '../errorCodes';
import { ApiConfig, HandleHttpClientRequest, RequestParams } from '../generated/http-client';
import { getBearerToken } from '../utils';

const hasErrorCode = (obj: Record<string, unknown>): obj is { errorCode: string } => {
  return 'errorCode' in obj && typeof obj.errorCode === 'string';
};

const checkErrorByCodes = (obj: unknown, errorCodes: string[]) => {
  return isObject(obj) && hasErrorCode(obj) && errorCodes.includes(obj.errorCode);
};

type GetApiConfigProps = {
  onTokenExpired?: () => Promise<string | null>;
  logout: () => Promise<void>;
  tenantProfileId?: number;
};

export const getApiConfig = (
  token: string | undefined | null,
  { logout, onTokenExpired, tenantProfileId }: GetApiConfigProps,
): ApiConfig<unknown> => {
  const handleHttpClientRequest: HandleHttpClientRequest<unknown, unknown> = async (
    data,
    { request },
  ) => {
    if (checkErrorByCodes(data.error, [ERROR_CODES.NEW_AUTH_ACCESS_TOKEN_EXPIRED])) {
      if (!onTokenExpired) {
        await logout();
        return null;
      }

      const newToken = await onTokenExpired();

      if (!newToken) {
        await logout();
        return null;
      }

      return request(getBearerToken(newToken));
    }

    if (
      checkErrorByCodes(data.error, [
        ERROR_CODES.NEW_AUTH_UNAUTHORIZED,
        ERROR_CODES.INVALID_SESSION,
      ])
    ) {
      await logout();
      return null;
    }

    return data;
  };

  const headers: RequestParams['headers'] = {
    'X-Client-Version': clientConfig.api.clientVersionHeader,
    'X-Client-Platform': clientConfig.api.clientPlatformHeader,
  };

  if (token) {
    headers.authorization = token;
  }

  if (tenantProfileId) {
    headers['X-Tenant-Profile-Id'] = String(tenantProfileId);
  }

  const config: ApiConfig = {
    baseUrl: apiConfig.url,
    baseApiParams: { headers },
    handleRequest: handleHttpClientRequest,
    customFetch: dpopFetch,
  };

  return config;
};
